import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import classNames from 'classnames';

import {
	TextField,
	Checkbox,
	Typography,
	MenuItem,
	Theme,
	Box,
	Popover,
	InputAdornment,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Autocomplete, {
	AutocompleteRenderInputParams,
	AutocompleteRenderGroupParams,
	AutocompleteChangeReason,
	AutocompleteChangeDetails,
	AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';

import { ProductApi, Category } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import {
	Option,
	getOptionsForActiveCategories,
	getOptionsForProducts,
	filterOptions,
	getProductsFromOptions,
	getOptionLabel,
	getOptionCategory,
} from '../utils/optionUtils';

import * as StockSelectors from 'selectors/StockSelectors';
import * as LanguageSelectors from 'selectors/LanguageSelectors';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface Props {
	value: {
		products: ProductApi[];
		categories: Category[];
	};
	onChange: (args: { products: ProductApi[]; categories: Category[] }) => void;
}

const ProductsCategorySelect = (props: Props) => {
	const { value, onChange } = props;
	const { t } = useTranslation();
	const classes = useStyles();

	const stockProducts = useSelector(StockSelectors.stockProductsData);
	const categories = useSelector(StockSelectors.categoriesData);
	const lang = useSelector(LanguageSelectors.language);

	const options = useMemo(() => getOptionsForActiveCategories(categories, stockProducts, lang), [
		categories,
		stockProducts,
		lang,
	]);

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [open, setOpen] = useState(false);
	const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
	const [searchInput, setSearchInput] = useState<string>('');

	const valueOptions = useMemo(() => getOptionsForProducts(value.products, options), [
		value,
		options,
	]);

	const handleProductsChange = (
		_event: React.ChangeEvent<{}>,
		_selectedOptions: Option[],
		reason: AutocompleteChangeReason,
		details?: AutocompleteChangeDetails<Option>,
	) => {
		if (reason === 'selectOption' && details) {
			const addedOptions = options.filter((option) => option.id === details.option.id);
			onChange({
				categories: value.categories,
				products: getProductsFromOptions(stockProducts, [...valueOptions, ...addedOptions]),
			});
		}
		if (reason === 'removeOption' && details) {
			const updatedValueOptions = valueOptions.filter((option) => option.id !== details.option.id);
			onChange({
				categories: value.categories,
				products: getProductsFromOptions(stockProducts, updatedValueOptions),
			});
		}
	};

	const handleInputChange = (
		_event: React.ChangeEvent<{}>,
		inputValue: string,
		reason: AutocompleteInputChangeReason,
	) => {
		if (reason === 'reset') {
			return;
		}
		setSearchInput(inputValue);
		const newFilteredOptions = filterOptions(options, { inputValue, getOptionLabel });
		if (!isEqual(filteredOptions, newFilteredOptions)) {
			setFilteredOptions(newFilteredOptions);
		}
	};

	const handleOpenPopup = () => {
		setSearchInput('');
		setFilteredOptions(options);
		setOpen(true);
	};

	const handleClosePopup = () => setOpen(false);

	const handleCategoryClick = (categoryId: string) => {
		if (value.categories.find((c) => c.id === categoryId)) {
			onChange({
				categories: value.categories.filter((v) => v.id !== categoryId),
				products: value.products,
			});
		} else {
			const category = categories.find((c) => c.id === categoryId);
			if (!category) return;
			onChange({
				categories: [...value.categories, category],
				products: value.products,
			});
		}
	};

	const renderOption = (
		props: React.HTMLAttributes<HTMLLIElement>,
		option: Option,
		{ selected }: { selected: boolean },
	) => {
		const categorySelected = !!value.categories.find((c) => c.id === option.categoryId);
		return (
			<MenuItem key={option.id} {...props} disableGutters>
				<Checkbox
					icon={icon}
					color="primary"
					checkedIcon={checkedIcon}
					className={classes.productCheckbox}
					checked={selected || categorySelected}
					disabled={categorySelected}
				/>
				<Typography variant="body1">{option.label}</Typography>
			</MenuItem>
		);
	};

	const renderGroup = (params: AutocompleteRenderGroupParams) => {
		const categoryLabel =
			options.find((option) => option.categoryId === params.group)?.categoryLabel ?? '';
		const isCategorySelected = !!value.categories.find((c) => c.id === params.group);
		return (
			<React.Fragment key={params.key}>
				<MenuItem onClick={() => handleCategoryClick(params.group)}>
					<Checkbox
						icon={icon}
						color="primary"
						checkedIcon={checkedIcon}
						className={classes.categoryCheckbox}
						checked={isCategorySelected}
					/>
					<Typography variant="body1">{categoryLabel}</Typography>
				</MenuItem>
				{params.children}
			</React.Fragment>
		);
	};

	const renderInput = (params: AutocompleteRenderInputParams) => (
		<>
			<TextField
				variant="standard"
				ref={params.InputProps.ref}
				inputProps={params.inputProps}
				placeholder={t('rentals.search.byProductName', 'Search for product name')}
				fullWidth
				autoFocus
				margin="normal"
				className={classes.inputSearchRoot}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon />
						</InputAdornment>
					),
				}}
			/>
		</>
	);

	const isProductsCategorySelected = (option: Option) => {
		return !!value.categories.find((c) => c.id === option.categoryId);
	};

	return (
		<>
			<div ref={setAnchorEl} onClick={handleOpenPopup} className={classes.select}>
				<Box pr={4}>
					<Typography className={classes.filterHeader}>{t('products.products')}</Typography>
				</Box>
				<ArrowDropDownIcon
					className={classNames(classes.popupIndicator, {
						[classes.popupIndicatorOpen]: open,
					})}
				/>
			</div>
			<Popover
				open={open}
				onClose={handleClosePopup}
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				classes={{
					paper: classes.popoverPaper,
				}}
			>
				<Autocomplete
					open={open}
					autoComplete
					disablePortal={true}
					disableCloseOnSelect
					multiple
					id="products-with-category-select"
					options={options}
					value={valueOptions}
					inputValue={searchInput}
					getOptionLabel={getOptionLabel}
					getOptionDisabled={isProductsCategorySelected}
					groupBy={getOptionCategory}
					filterOptions={filterOptions}
					onChange={handleProductsChange}
					onInputChange={handleInputChange}
					renderGroup={renderGroup}
					renderOption={renderOption}
					renderInput={renderInput}
					classes={{
						paper: classes.autocompletePaper,
						popperDisablePortal: classes.popperDisablePortal,
						listbox: classes.listbox,
						option: classes.option,
					}}
				/>
			</Popover>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		productCheckbox: {
			marginLeft: 38,
			marginRight: theme.spacing(1),
		},
		categoryCheckbox: {
			marginRight: theme.spacing(1),
		},
		closeIcon: {
			marginRight: theme.spacing(-0.25),
			padding: theme.spacing(0.5),
			color: theme.palette.action.active,
		},
		popupIndicator: {
			color: theme.palette.action.active,
			top: `calc(50% - 12px)`,
			right: theme.spacing(1),
			position: 'absolute',
			pointerEvents: 'none',
		},
		popupIndicatorOpen: {
			transform: 'rotate(180deg)',
		},
		popoverPaper: {
			width: 336,
			marginTop: 1,
			overflow: 'visible',
		},
		autocompletePaper: {
			boxShadow: 'none',
			margin: 0,
			width: '100%',
		},
		popperDisablePortal: {
			position: 'relative !important' as 'relative',
			width: '100%',
			transform: 'translate(0,0) !important',
		},
		inputSearchRoot: {
			padding: theme.spacing(0, 3),
			'& input': {
				backgroundColor: theme.palette.background.paper,
				padding: theme.spacing(1),
			},
		},
		select: {
			position: 'relative',
			height: 38,
			width: '100%',
			cursor: 'pointer',
			display: 'inline-flex',
			alignItems: 'center',
		},
		option: {
			'&[aria-selected="true"]': {
				backgroundColor: theme.palette.background.paper,
			},
			'&[data-focus="true"]': {
				backgroundColor: theme.palette.action.hover,
			},
			whiteSpace: 'initial',
		},
		category: {
			backgroundColor: theme.palette.action.hover,
		},
		listbox: {
			maxHeight: '35vh',
			paddingTop: 0,
		},
		filterHeader: {
			fontSize: 14,
			fontWeight: 400,
		},
	}),
);

export default ProductsCategorySelect;
